import { PDFDocument, rgb } from 'pdf-lib'
import templateUrl from './template.pdf'
import { drawLogos } from "@/reports/shared_utils/drawLogos";
import { collectUnsupportedHighlights, drawUnsupportedHighlights } from "@/reports/shared_utils/highlightUnsupported";
import { sanitizeForPdf } from "@/reports/shared_utils/sanitizeForPdf";

// Maps each schema key to a representative PDF field name. Used by the live
// preview pane to find a widget rectangle for highlight/scroll targeting.
export const SCHEMA_TO_PDF_FIELD = {
  date: 'date',
  studentName: 'student',
  oen: 'oen',
  year: 'year1',
  daysAbsent: 'daysAbsent',
  totalDaysAbsent: 'totalDaysAbsent',
  timesLate: 'timesLate',
  totalTimesLate: 'totalTimesLate',
  frenchProgram: 'frenchImmersion',
  teacher: 'teacher',
  earlyChildhoodEducator: 'earlyChildhoodEducator',
  principal: 'principal',
  telephone: 'tel',
  board: 'board',
  school: 'school',
  schoolAddress: 'schoolAddress',
  boardAddress: 'boardAddress',
  esl: 'esl',
  iep: 'iep',
  keyLearningComments: 'keyLearningComments',
  boardInformation: 'boardInfo',
  eceNames: 'eceNames',
}

export const PAGE_1_SCHEMA_FIELDS = new Set([
  'date', 'studentName', 'oen', 'year', 'daysAbsent', 'totalDaysAbsent',
  'timesLate', 'totalTimesLate', 'frenchProgram',
  'teacher', 'earlyChildhoodEducator', 'principal', 'telephone',
  'board', 'school', 'schoolAddress', 'boardAddress',
  'esl', 'iep', 'keyLearningComments',
])

export const STEP_ANCHOR_FIELDS = {
  1: 'student',
  2: 'teacher',
  3: 'keyLearningComments',
  4: 'boardInfo',
}

const val = (v) => sanitizeForPdf(v);
const isTrue = (v) => v === true || v === 'true' || v === 'on'

const fill = async (data, highlightRect = null, logos = []) => {
  const pdfBytes = await fetch(templateUrl).then((r) => r.arrayBuffer())
  const pdfDoc = await PDFDocument.load(pdfBytes)
  const form = pdfDoc.getForm()

  const text = (name, value, fontSize) => {
    const f = form.getTextField(name)
    f.setText(val(value))
    if (fontSize != null) f.setFontSize(fontSize)
  }
  const checkIf = (name, condition) => {
    const f = form.getCheckBox(name)
    condition ? f.check() : f.uncheck()
  }

  // ----- Page 1 -----
  text('date', data.date, 10)
  text('student', data.studentName)
  text('oen', data.oen)
  text('daysAbsent', data.daysAbsent)
  text('totalDaysAbsent', data.totalDaysAbsent)
  text('timesLate', data.timesLate)
  text('totalTimesLate', data.totalTimesLate)
  text('teacher', data.teacher)
  text('earlyChildhoodEducator', data.earlyChildhoodEducator)
  text('principal', data.principal)
  text('tel', data.telephone)
  text('board', data.board)
  text('school', data.school)
  text('schoolAddress', data.schoolAddress)
  text('boardAddress', data.boardAddress)
  text('keyLearningComments', data.keyLearningComments)
  form.getTextField('keyLearningComments').enableMultiline()

  checkIf('year1', data.year === 'Year 1')
  checkIf('year2', data.year === 'Year 2')
  checkIf('frenchImmersion', data.frenchProgram === 'Immersion')
  checkIf('frenchCore',      data.frenchProgram === 'Core')
  checkIf('frenchExtended',  data.frenchProgram === 'Extended')
  checkIf('esl', isTrue(data.esl))
  checkIf('iep', isTrue(data.iep))

  // ----- Page 2 -----
  text('studentP2', data.studentName)
  text('oenP2', data.oen)
  checkIf('year1P2', data.year === 'Year 1')
  checkIf('year2P2', data.year === 'Year 2')
  text('boardInfo', data.boardInformation, 10)
  form.getTextField('boardInfo').enableMultiline()
  text('eceNames', data.eceNames)

  const __unsupportedHL = collectUnsupportedHighlights(pdfDoc, form);
  form.flatten()

  if (highlightRect) {
    const { pageIndex, x, y, width, height } = highlightRect
    const pages = pdfDoc.getPages()
    if (pages[pageIndex]) {
      pages[pageIndex].drawRectangle({
        x: x - 2, y: y - 2,
        width: width + 4, height: height + 4,
        color: rgb(0, 0.95, 0),
        opacity: 0.25,
        borderWidth: 0,
      })
    }
  }

  // Class logos: drawn last so they sit on top of the flattened template.
  drawUnsupportedHighlights(pdfDoc, __unsupportedHL);
  await drawLogos(pdfDoc, logos);

  const out = await pdfDoc.save()
  const blob = new Blob([out], { type: 'application/pdf' })
  return URL.createObjectURL(blob)
}

export { fill }
